import { useState } from 'react'
import { Banner, EmptyState } from '@/ui'
import { errorMessage } from '../../api/core'
import { usePlanIssue, type RunDetail } from '../../api/process'
import { useStock } from '../../api/store'
import { issueCandidates } from '../../model/process/issuePlan'
import { FormDialog } from '../FormDialog'
import { Code, Kg } from '../bits'

/** Pick the lots whose bags the store keeper hands to this run (an issue task on the keeper's phone). */
export function IssueToRunDialog({ run, open, onClose }: { run: RunDetail; open: boolean; onClose: () => void }) {
  const [picked, setPicked] = useState<string[]>([])
  const [error, setError] = useState<string | null>(null)
  const stock = useStock({ siteId: run.siteId ?? undefined, page: 1, limit: 200 })
  const lots = issueCandidates(stock.data?.rows ?? [], run)
  const plan = usePlanIssue(run.id, (e) => {
    setError(errorMessage(e))
    return true
  })
  const toggle = (id: string) => setPicked((p) => (p.includes(id) ? p.filter((x) => x !== id) : [...p, id]))
  return (
    <FormDialog
      open={open}
      onClose={() => {
        setPicked([])
        setError(null)
        onClose()
      }}
      title="Issue lots to this run"
      description="The store keeper scans and weighs each bag of these lots on the phone and moves them to the machine."
      saveLabel="Send to the store keeper"
      canSave={picked.length > 0}
      loading={plan.isPending}
      onSave={async () => {
        setError(null)
        await plan.mutateAsync(picked)
        setPicked([])
      }}
    >
      {error && <Banner tone="error" title="Not planned">{error}</Banner>}
      {stock.isLoading ? (
        <p className="text-[14px] text-muted">Looking for lots at the site…</p>
      ) : lots.length === 0 ? (
        <EmptyState title="No lots free at this site" description="Lots must be at the run's site, of its crop, and not on hold." />
      ) : (
        <ul className="flex flex-col divide-y divide-rule rounded-card border border-rule">
          {lots.map((l) => (
            <li key={l.unitId}>
              <label className="flex cursor-pointer items-center gap-3 px-3 py-2.5 text-[14px]">
                <input type="checkbox" checked={picked.includes(l.unitId)} onChange={() => toggle(l.unitId)} />
                <span className="flex min-w-0 flex-1 flex-col">
                  <Code>{l.unitCode}</Code>
                  <span className="text-[12px] text-muted">
                    {l.farmerCode ?? 'no farmer'} · {l.places.join(', ') || 'site'}
                    {l.unitId === run.originUnitId ? ' · the run’s lot' : ''}
                  </span>
                </span>
                <span className="text-right font-mono text-[13px]">
                  {l.bags} bags · <Kg grams={l.kgNowG} />
                </span>
              </label>
            </li>
          ))}
        </ul>
      )}
    </FormDialog>
  )
}
